import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { Z_INDEX } from '@constants/z-index';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ActionButton } from '@pages/ActionButton';
import { BaseModal } from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import classNames from 'classnames';
import css from './EditWordsModal.module.scss';
import shell from './LibraryModalShell.module.scss';
import { WordHintsEditor } from './WordHintsEditor';

const TITLE = 'Edit word hints';
const SUBTITLE =
  'Add words that come up in your media, like names, brands, or jargon, so StarZero transcribes them accurately.';

type EditWordsModalProps = {
  open: boolean;
  onClose: () => void;
  words: string[];
  onChange: (words: string[]) => void;
  /**
   * Show a note that edits only apply to media added afterward. The caller decides
   * when to pass it — only for an EXISTING library that already has media (editing a
   * brand-new / empty library has nothing to re-transcribe, so no warning).
   */
  showAppliesToNewMediaWarning?: boolean;
};

/**
 * Editor for a library's custom word hints (transcription glossary), opened from
 * the "Edit words" button on the create-library word-hints toggle. Desktop: a
 * centered modal. Mobile: a bottom-sheet drawer stacked ABOVE the create drawer
 * (z-index bumped past the drawer's fixed 1400 via Z_INDEX.aboveMobileDrawer).
 */
export function EditWordsModal({
  open,
  onClose,
  words,
  onChange,
  showAppliesToNewMediaWarning,
}: EditWordsModalProps) {
  const isMobile = useIsMobile();

  const body = (
    <>
      {showAppliesToNewMediaWarning && (
        <p className={css.warning}>
          <WarningAmberRoundedIcon sx={{ fontSize: 16, flexShrink: 0, mt: '1px' }} />
          Only media added after this change will use these words; media already in this library
          won't be re-transcribed.
        </p>
      )}
      <WordHintsEditor words={words} onChange={onChange} />
      <div className={shell.actions}>
        <ActionButton type="button" onClick={onClose} fullWidth={isMobile}>
          Done
        </ActionButton>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <MobileDrawer
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        frosted
        // Stacked above whichever surface opened it (the create-library drawer, or the
        // edit-library sheet in-library) via aboveMobileDrawer. Skip the double blur — the
        // create drawer is already blurred; on the opaque edit sheet the blur is just moot.
        noBackdropBlur
        zIndex={Z_INDEX.aboveMobileDrawer}
      >
        <div className={css.drawerContent}>
          <h2 className={css.title}>{TITLE}</h2>
          <p className={css.subtitle}>{SUBTITLE}</p>
          {body}
        </div>
      </MobileDrawer>
    );
  }

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      className={classNames(shell.modal, shell.narrow)}
      title={TITLE}
      subtitle={SUBTITLE}
    >
      {body}
    </BaseModal>
  );
}
